(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.dataset.motion === 'false';

  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', class extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        this.track = this.querySelector('[data-gallery-track]');
        if (!this.track) return;
        this.addEventListener('click', (event) => {
          const thumbnail = event.target.closest('[data-gallery-target]');
          if (thumbnail) {
            event.preventDefault();
            this.selectMedia(thumbnail.dataset.galleryTarget, true);
          }
          const zoom = event.target.closest('[data-product-zoom]');
          if (zoom) {
            event.preventDefault();
            const dialog = this.querySelector('dialog');
            const content = dialog.querySelector('[data-zoom-content]');
            const image = new Image();
            image.src = zoom.href;
            image.alt = zoom.dataset.zoomLabel || '';
            content.replaceChildren(image);
            if (window.Desire?.openDialog) window.Desire.openDialog(dialog.id, zoom);
            else {
              dialog.showModal();
              dialog.addEventListener('close', () => zoom.focus({preventScroll: true}), {once: true});
            }
          }
          if (event.target.closest('[data-close-dialog]') && !window.Desire?.closeDialog) this.querySelector('dialog').close();
        }, {signal: this.abort.signal});
        this.track.addEventListener('keydown', (event) => {
          if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const slides = [...this.track.querySelectorAll('[data-media-id]')];
          if (!slides.length) return;
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.selectMedia(slides[Math.max(0, Math.min(slides.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1)))].dataset.mediaId, true);
        }, {signal: this.abort.signal});
        this.observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < .6) continue;
            const id = entry.target.dataset.mediaId;
            this.querySelectorAll('[data-gallery-target]').forEach((item) => {
              const selected = item.dataset.galleryTarget === id;
              item.classList.toggle('is-active', selected);
              if (selected) item.setAttribute('aria-current', 'true');
              else item.removeAttribute('aria-current');
            });
            this.track.querySelectorAll('video').forEach((video) => { if (!entry.target.contains(video)) video.pause(); });
            this.track.querySelectorAll('iframe').forEach((frame) => {
              if (entry.target.contains(frame)) return;
              try {
                const origin = new URL(frame.src).origin;
                if (origin.includes('youtube.com') || origin.includes('youtube-nocookie.com')) frame.contentWindow?.postMessage(JSON.stringify({event: 'command', func: 'pauseVideo', args: []}), origin);
                else if (origin.includes('vimeo.com')) frame.contentWindow?.postMessage(JSON.stringify({method: 'pause'}), origin);
              } catch (_) {}
            });
          }
        }, {root: this.track, threshold: [.6]});
        this.track.querySelectorAll('[data-media-id]').forEach((slide) => this.observer.observe(slide));
      }
      selectMedia(id, animate = false) {
        const slide = [...this.track.querySelectorAll('[data-media-id]')].find((item) => item.dataset.mediaId === String(id));
        if (!slide) return;
        const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
        this.track.scrollTo({left, behavior: animate && !reducedMotion() ? 'smooth' : 'instant'});
      }
      disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
    });
  }

  if (!customElements.get('product-info')) {
    customElements.define('product-info', class extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        this.form = this.querySelector('[data-product-form]');
        if (!this.form) return;
        this.requestNumber = 0;
        this.variantPending = false;
        this.form.addEventListener('submit', (event) => {
          if (!this.variantPending) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, {capture: true, signal: this.abort.signal});
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-option-value-id]')) this.updateVariant(event.target);
          if (event.target.matches('[data-selling-plan]')) this.updatePlan(true);
        }, {signal: this.abort.signal});
        this.form.addEventListener('formdata', (event) => {
          if (event.formData.get('selling_plan') === '') event.formData.delete('selling_plan');
          for (const key of [...event.formData.keys()]) if (key.startsWith('option-')) event.formData.delete(key);
        }, {signal: this.abort.signal});
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-product-quantity]');
          if (!button) return;
          const input = this.form.elements.quantity;
          const min = Number(input.min || 1);
          const max = input.max ? Number(input.max) : Infinity;
          const step = Number(input.step || 1);
          const value = Number(input.value || min);
          input.value = Math.max(min, Math.min(max, value + Number(button.dataset.productQuantity) * step));
          input.dispatchEvent(new Event('change', {bubbles: true}));
        }, {signal: this.abort.signal});
        this.observeSticky();
      }
      observeSticky() {
        this.stickyObserver?.disconnect();
        const button = this.querySelector('.product-buy-row');
        if (!button) return;
        this.stickyObserver = new IntersectionObserver(([entry]) => {
          this.stickyVisible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.querySelector('[data-sticky-purchase]')?.classList.toggle('is-visible', this.stickyVisible);
        }, {threshold: 0});
        this.stickyObserver.observe(button);
      }
      async updateVariant(control) {
        const productURL = new URL(control.dataset.productUrl || this.dataset.productUrl, location.origin);
        const currentProduct = new URL(this.dataset.productUrl, location.origin);
        const optionValues = [...this.querySelectorAll('[data-option-value-id]:checked')].map((input) => input.dataset.optionValueId).join(',');
        productURL.searchParams.set('option_values', optionValues);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) productURL.searchParams.set('preview_theme_id', preview);
        if (productURL.pathname !== currentProduct.pathname) {
          location.assign(productURL.href);
          return;
        }
        const requestNumber = ++this.requestNumber;
        this.variantRequest?.abort();
        this.variantRequest = new AbortController();
        productURL.searchParams.set('section_id', this.dataset.sectionId);
        const oldPlan = this.querySelector('[name="selling_plan"]:checked')?.value;
        if (oldPlan) productURL.searchParams.set('selling_plan', oldPlan);
        this.variantPending = true;
        this.form.querySelector('[name="id"]').disabled = true;
        const payment = this.querySelector('[data-variant-part="payment"]');
        if (payment) payment.hidden = true;
        this.querySelectorAll('[data-product-submit]').forEach((button) => { button.disabled = true; button.dataset.variantDisabled = 'true'; });
        this.form.setAttribute('aria-busy', 'true');
        const error = this.form.querySelector('[data-product-error]');
        if (error) { error.hidden = true; error.textContent = ''; }
        const activeId = document.activeElement?.id;
        try {
          const response = await fetch(productURL, {credentials: 'same-origin', signal: this.variantRequest.signal});
          if (!response.ok) throw new Error('Variant section failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (requestNumber !== this.requestNumber) return;
          const next = html.querySelector('product-info');
          const stateNode = next?.querySelector('[data-product-state]');
          if (!stateNode) throw new Error('Variant state missing');
          const state = JSON.parse(stateNode.textContent);
          const variantInput = this.form.querySelector('[name="id"]');
          variantInput.value = state.id || '';
          variantInput.disabled = !state.id;
          const parts = ['options', 'price', 'purchase', 'button', 'payment', 'availability', 'sticky'];
          for (const part of parts) {
            const existing = this.querySelector(`[data-variant-part="${part}"]`);
            const replacement = next.querySelector(`[data-variant-part="${part}"]`);
            if (existing && replacement) existing.replaceWith(replacement.cloneNode(true));
          }
          const quantity = this.form.elements.quantity;
          const min = Number(state.min || 1);
          const max = state.max ? Number(state.max) : Infinity;
          const step = Number(state.step || 1);
          quantity.min = min;
          quantity.step = step;
          if (state.max) quantity.max = state.max;
          else quantity.removeAttribute('max');
          quantity.value = Math.min(max, Math.max(min, min + Math.floor((Number(quantity.value || min) - min) / step) * step));
          this.querySelectorAll('[data-product-submit]').forEach((button) => { button.dataset.variantDisabled = String(!state.available); button.disabled = !state.available; });
          this.variantPending = false;
          if (state.mediaId) this.querySelector('product-gallery')?.selectMedia(state.mediaId);
          const url = new URL(location.href);
          url.searchParams.delete('option_values');
          if (state.id) url.searchParams.set('variant', state.id);
          else { url.searchParams.delete('variant'); url.searchParams.set('option_values', optionValues); }
          const plan = this.querySelector('[name="selling_plan"]:checked')?.value;
          if (plan) url.searchParams.set('selling_plan', plan);
          else url.searchParams.delete('selling_plan');
          history.replaceState({}, '', url);
          variantInput.dispatchEvent(new Event('change', {bubbles: true}));
          this.dispatchEvent(new CustomEvent('desire:variant-change', {bubbles: true, detail: {variantId: state.id, productId: state.productId, formId: this.dataset.formId}}));
          this.updatePlan(false);
          this.querySelector('[data-sticky-purchase]')?.classList.toggle('is-visible', this.stickyVisible);
          if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
          if (activeId) this.querySelector(`#${CSS.escape(activeId)}`)?.focus({preventScroll: true});
          const priceText = this.querySelector('[data-product-price]')?.textContent.trim();
          const availability = this.querySelector('[data-variant-part="availability"]')?.textContent.trim();
          this.querySelector('[data-variant-status]').textContent = [priceText, availability].filter(Boolean).join('. ');
        } catch (failure) {
          if (failure.name === 'AbortError' || requestNumber !== this.requestNumber) return;
          if (error) { error.textContent = this.dataset.errorMessage; error.hidden = false; }
        } finally {
          if (requestNumber === this.requestNumber) this.form.removeAttribute('aria-busy');
        }
      }
      updatePlan(updateURL) {
        const plan = this.querySelector('[name="selling_plan"]:checked')?.value || '';
        const template = [...this.querySelectorAll('template[data-plan-price]')].find((item) => item.dataset.planPrice === plan);
        if (template) this.querySelectorAll('[data-product-price]').forEach((price) => price.replaceChildren(template.content.cloneNode(true)));
        if (updateURL) {
          const status=this.querySelector('[data-variant-status]');
          if(status)status.textContent=this.querySelector('[data-product-price]')?.textContent.trim() || '';
          const url = new URL(location.href);
          if (plan) url.searchParams.set('selling_plan', plan);
          else url.searchParams.delete('selling_plan');
          history.replaceState({}, '', url);
        }
      }
      disconnectedCallback() { this.abort?.abort(); this.variantRequest?.abort(); this.stickyObserver?.disconnect(); }
    });
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        if (this.querySelector('.product-recommendations__grid') || !this.dataset.url) return;
        this.abort = new AbortController();
        this.observer = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          this.observer.disconnect();
          this.load();
        }, {rootMargin: '300px'});
        this.observer.observe(this.closest('.shopify-section') || this.parentElement);
      }
      async load() {
        try {
          const response = await fetch(this.dataset.url, {credentials: 'same-origin', signal: this.abort.signal});
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = html.querySelector('product-recommendations');
          if (!next?.querySelector('.product-recommendations__grid')) return;
          this.innerHTML = next.innerHTML;
          this.hidden = false;
        } catch (_) {}
      }
      disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
    });
  }
})();
